import { get } from 'svelte/store';
import { Liquid } from 'liquidjs';

import { getTemplateDefaults } from '@client/stores/bizcard';
import { isBambooHR, isGoogle, isMicrosoft, isOutlookVisualPreview } from '@client/stores/domain-settings';
import { userStore } from '@client/stores/user';

import { generateUniqueRulename } from '@client/utils/rulenames';
import { fixImagesInline } from '@client/utils/signatures';

import { SIGNATURE_DEFAULTS } from '@client/enums';

import { buildBrandTokens, resolveSignaturePlatform, stripHash } from './shared';
import template1 from './template-1';
import template2 from './template-2';
import template3 from './template-3';
import template4 from './template-4';
import template5 from './template-5';

// Re-export the pure helpers moved to ./shared so consumers keep importing them from the
// module root (public API is unchanged).
export { buildBrandTokens, resolveSignaturePlatform, stripHash };

// Vanilla Liquid engine (no custom filters/tags) — mirrors the bizcard preview precedent
// (src/client/stores/bizcard-templates/index.js). Used only to render gallery thumbnails.
const engine = new Liquid();

// Sample sender used for the gallery preview photo path. Kept local (not in SIGNATURE_DEFAULTS)
// so the placeholder sender never leaks into a production signature seed.
const PREVIEW_SENDER = 'john.smith';

/**
 * Factory returning the 5 starter templates with tenant branding baked into each source.
 * @param {object} [defaults] - Brand defaults; defaults to getTemplateDefaults().
 * @returns {Array<{ id: number, name: string, height: number, params: string[], source: string }>}
 */
export const getSignatureTemplates = (defaults = getTemplateDefaults()) => {
  const brand = buildBrandTokens(defaults);

  return [template1(brand), template2(brand), template3(brand), template4(brand), template5(brand)];
};

/**
 * Maps a template's param-name array to the tag-block payload shape.
 * @param {{ params?: string[] }} template
 * @returns {Array<{ name: string, hide: boolean, transform: boolean, default: string }>}
 */
export const buildSignatureParams = (template) =>
  (template?.params || []).map((name) => ({ name, hide: false, transform: false, default: '' }));

/**
 * Builds the creation-time signature seed from a template's source.
 * @param {{ source?: string }} template
 * @param {{ domain?: string, platform?: string, isOutlookVisualPreview?: boolean }} options
 * @returns {string}
 */
export const buildSignatureSeed = (template, { domain, platform = 'office365', isOutlookVisualPreview } = {}) => {
  const result = (template?.source || '').replace(/\{\{domain\}\}/g, domain).replace(/__PLATFORM__/g, platform);

  return isOutlookVisualPreview ? fixImagesInline(result) : result;
};

/**
 * Preview-only transform: replaces the provider-photo src (anchored on the __PLATFORM__ sentinel)
 * — both the <img src> and the Outlook VML <v:fill src> — with the headshot.svg placeholder so the
 * gallery thumbnail shows a photo without ever putting the placeholder into a production seed.
 * Anchoring on __PLATFORM__ guarantees logo/social/CTA images are never touched. No-op for T1/T2.
 * @param {string} source
 * @returns {string}
 */
const swapHeadshotToPlaceholder = (source) =>
  source.replace(
    /(src\s*=\s*)(["'])([^"']*__PLATFORM__[^"']*)\2/gi,
    (_, prefix, quote) => `${prefix}${quote}${SIGNATURE_DEFAULTS.photo}${quote}`
  );

/**
 * Async gallery-preview helper (mirrors loadNewBizcardModalTemplates). Renders each brand-baked
 * source through Liquid with sample data so every {% if %} block evaluates and no gated element
 * vanishes from the thumbnail. Returns objects carrying BOTH the creation source and rendered html.
 *
 * Photo behaviour mirrors the business-card gallery:
 * - Previewing your own domain (`isManaging` false) → the current user's REAL identity and their
 *   real provider photo (`/photo/<email>/<platform>.png`); an empty result if they have none.
 * - Managing a customer domain (`isManaging` true) → mock sample data and the static headshot.svg
 *   placeholder (the bizcard `example.jpg` mock is not a verified served asset, so we use our
 *   known static image). The placeholder is preview-only and never enters a production seed.
 * @param {object} [defaults] - Brand defaults; defaults to getTemplateDefaults().
 * @param {{ domain?: string, cname?: [string, string], isManaging?: boolean, currentUser?: object, platform?: string }} [context]
 * @returns {Promise<Array<{ id: number, name: string, height: number, params: string[], source: string, html: string }>>}
 */
export const loadSignatureModalTemplates = async (
  defaults = getTemplateDefaults(),
  { domain, cname, isManaging = false, currentUser = {}, platform = 'office365' } = {}
) => {
  const branded = getSignatureTemplates(defaults);
  const [sgcnameproto, sgcname] = cname || [];

  // Self-preview uses the current user's real identity (falling back to sample values for any
  // field the user store doesn't carry); managing uses the sample identity as-is.
  const previewUser = isManaging
    ? {}
    : {
        name: currentUser.name || SIGNATURE_DEFAULTS.name,
        title: currentUser.title || SIGNATURE_DEFAULTS.title,
        email: currentUser.email || SIGNATURE_DEFAULTS.email,
        mobile: currentUser.mobile || SIGNATURE_DEFAULTS.mobile,
        office: currentUser.office || SIGNATURE_DEFAULTS.office,
        phone: currentUser.phone || SIGNATURE_DEFAULTS.phone
      };

  const previewData = {
    ...SIGNATURE_DEFAULTS,
    ...previewUser,
    domain,
    // Templates render {{ website }} (not {{domain}}); without this the thumbnail would show the
    // Opensense sample website for every tenant. Preview-only — the created signature uses the
    // user's real `website` param.
    website: domain ? `https://${domain}` : SIGNATURE_DEFAULTS.website,
    sgcnameproto,
    sgcname,
    // Signatures key the provider photo on the sender EMAIL; use the current user's own email
    // when previewing self (managing swaps the photo for the placeholder, so sgsender is moot).
    sgsender: isManaging ? PREVIEW_SENDER : currentUser.email || PREVIEW_SENDER
  };

  return Promise.all(
    branded.map(async (template) => {
      // Managing → static placeholder (no real face for a domain that isn't yours);
      // self → resolve the real provider photo path so you see your own photo.
      const previewSource = isManaging
        ? swapHeadshotToPlaceholder(template.source)
        : template.source.replace(/__PLATFORM__/g, platform);
      const html = await engine.render(engine.parse(previewSource), previewData);

      return { ...template, html };
    })
  );
};

/**
 * Resolves the provider photo-path segment from the CURRENT domain's provider flags.
 * @returns {'office365' | 'google' | 'bamboohr'}
 */
const currentPlatform = () =>
  resolveSignaturePlatform({
    isMicrosoft: get(isMicrosoft),
    isGoogle: get(isGoogle),
    isBambooHR: get(isBambooHR)
  });

/**
 * Loads the brand-baked gallery previews for the CURRENT user/domain, reading the identity
 * and provider flags from the app stores so a page doesn't assemble this context itself.
 * @returns {Promise<Array<{ id: number, name: string, height: number, params: string[], source: string, html: string }>>}
 */
export const loadStudioTemplatePreviews = () => {
  const user = get(userStore);

  return loadSignatureModalTemplates(getTemplateDefaults(), {
    domain: user.domain,
    cname: user.cname,
    isManaging: user.isManaging,
    currentUser: user.currentUser,
    platform: currentPlatform()
  });
};

/**
 * Builds the tag-block create payload for the "new signature" flow. With a template it rebuilds
 * the seed from the CURRENT brand at call time (by id) so a preview rendered earlier can never
 * bake a stale/fallback brand into the created signature; without one it returns the blank
 * payload. Reads identity/provider/outlook flags from the app stores.
 * @param {{ id: number } | null} [template]
 * @returns {{ rulename: string, htmltemplate: string, texttemplate: string, params: object[] }}
 */
export const buildTemplateSignatureData = (template) => {
  const data = {
    rulename: generateUniqueRulename('template'),
    htmltemplate: '',
    texttemplate: '',
    params: [{ name: 'blank', hide: false, transform: false, default: '' }]
  };

  if (template) {
    const freshTemplate = getSignatureTemplates().find(({ id }) => id === template.id);

    data.params = buildSignatureParams(freshTemplate);
    data.htmltemplate = buildSignatureSeed(freshTemplate, {
      domain: get(userStore).domain,
      platform: currentPlatform(),
      isOutlookVisualPreview: get(isOutlookVisualPreview)
    });
    data.texttemplate = '{{ name }}\n{{ title }}';
  }

  return data;
};

/**
 * Builds the editor-ready draft shape for the "new signature" flow. Wraps
 * buildTemplateSignatureData (which supplies rulename/htmltemplate/texttemplate/params and the
 * brand-baked seed) and adds the reply/text/enabled/archived defaults the editor reads before a
 * draft is persisted. The server's create endpoint supplies the remaining fields.
 * @param {{ id: number } | null} [template]
 * @returns {{ rulename: string, htmltemplate: string, texttemplate: string, params: object[], replyhtmltemplate: string, replytexttemplate: string, replytemplateenabled: boolean, archived: boolean }}
 */
export const buildDraftSignature = (template) => ({
  ...buildTemplateSignatureData(template),
  replyhtmltemplate: '',
  replytexttemplate: '',
  replytemplateenabled: false,
  archived: false
});
